import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearReferralCode } from "@/lib/referral";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  isAdmin: false,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

async function checkAdmin(userId: string): Promise<boolean> {
  try {
    const { data } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    return !!data;
  } catch {
    return false;
  }
}

async function linkPendingReferral(userId: string) {
  try {
    const pendingCode = localStorage.getItem("spoti_pending_referral");
    if (!pendingCode) return;
    // Check if already linked
    const { data: existing } = await supabase.from("referrals").select("id").eq("referred_id", userId).single();
    if (existing) { localStorage.removeItem("spoti_pending_referral"); clearReferralCode(); return; }
    // Find referral code
    const { data: codeRow } = await supabase.from("referral_codes").select("id, user_id").eq("code", pendingCode).single();
    if (!codeRow || codeRow.user_id === userId) { localStorage.removeItem("spoti_pending_referral"); return; }
    // Create referral link
    await supabase.from("referrals").insert({ referrer_id: codeRow.user_id, referred_id: userId, referral_code_id: codeRow.id });
    localStorage.removeItem("spoti_pending_referral");
    clearReferralCode();
  } catch {}
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      if (session?.user) {
        checkAdmin(session.user.id).then((admin) => {
          if (mounted) setIsAdmin(admin);
        });
      }
      setLoading(false);
    }).catch(() => {
      if (mounted) setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        setSession(session);
        if (session?.user) {
          checkAdmin(session.user.id).then((admin) => {
            if (mounted) setIsAdmin(admin);
          });
          // Link pending referral on first login after signup
          linkPendingReferral(session.user.id);
        } else {
          setIsAdmin(false);
        }
        // Don't set loading here — getSession handles it
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, isAdmin, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
